import React, { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./electricalApi";
import ElectricalReview from "./ElectricalReview";
import ElectricalScoringTable, { blankScoringRows, buildScoringSnapshot } from "./ElectricalScoringTable";

/**
 * Finish and submit. Modeled on Csa/CsaSubmit.js.
 *
 * Submit materialises the observations and freezes the scoring snapshot, so
 * it is the one action that cannot happen against a local copy. Two gates, in
 * this order:
 *
 *   1. Everything on this device has reached the server. Submitting with
 *      work still queued would freeze a snapshot missing answers the auditor
 *      already gave — and the snapshot is what the client's report says.
 *   2. The server's own readiness rules: every checkpoint answered, every gap
 *      written up, every photo assigned.
 *
 * Unused notes warn rather than block, because a note may have been a
 * reminder to self that was never a finding. Photos block, because an
 * unattached photo is evidence that belongs somewhere.
 */
export default function ElectricalSubmit({ auditId, audit, canReview, canReopen, onSubmitted, onChanged }) {
  const [readiness, setReadiness] = useState(null);
  const [sync, setSync] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [scoringRows, setScoringRows] = useState(blankScoringRows);
  const scoringLoaded = useRef(false);

  useEffect(() => api.subscribeSync(setSync), []);

  /*
   * Loaded once, from whatever the audit already has saved - not on every
   * prop change, or a re-render mid-edit (e.g. from the sync badge) would
   * overwrite numbers the auditor is still typing.
   */
  useEffect(() => {
    if (scoringLoaded.current) return;
    const saved = audit && audit.scoring_snapshot && audit.scoring_snapshot.parameters;
    if (saved && saved.length) {
      setScoringRows(saved);
      scoringLoaded.current = true;
    }
  }, [audit]);

  const check = useCallback(() => {
    setError(null);
    api
      .getReadiness(auditId)
      .then(setReadiness)
      .catch((e) => setError(e.message));
  }, [auditId]);

  useEffect(check, [check]);

  const syncFirst = async () => {
    setBusy(true);
    try {
      await api.flush(auditId);
      await api.resync(auditId);
      check();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const scoring_snapshot = buildScoringSnapshot(scoringRows);
      const r = await api.submitAudit(auditId, { scoring_snapshot });
      setDone({ ...r, scoring_snapshot: (r && r.scoring_snapshot) || scoring_snapshot });
      if (onSubmitted) onSubmitted(r);
    } catch (e) {
      setError(e.message);
      // A refusal usually means readiness moved under us — show why, now.
      check();
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="cap-writeup">
        <div className="wu-empty">
          <h2>Submitted</h2>
          <p>
            {done.overall_pct == null
              ? "This audit has been submitted."
              : `Overall conformance ${done.overall_pct}% — ${done.maturity_band}.`}
            {done.assessed
              ? ` ${done.assessed} checkpoints counted towards the score.`
              : ""}
          </p>
        </div>
      </div>
    );
  }

  /*
   * Once submitted, this tab becomes the review step rather than a dead end.
   * The scoring table and its gate were only ever there to get submit
   * unblocked - once submitted, they go the same way as the sync/readiness
   * gates and the submit button itself: gone, not shown read-only.
   */
  if (audit && audit.status !== "draft") {
    return (
      <ElectricalReview
        auditId={auditId}
        audit={audit}
        canReview={canReview}
        canReopen={canReopen}
        onChanged={onChanged}
      />
    );
  }

  const offline = sync && !sync.online;
  const unsynced = sync ? sync.pending : 0;
  /*
   * Photographs waiting to upload block submit as firmly as queued answers
   * do - they are not outbox entries, so `pending` does not see them, and
   * submitting freezes the projection, so any still on the device would be
   * missing from the report for good.
   */
  const photosWaiting = sync ? sync.photos_pending || 0 : 0;
  const blockedBySync = offline || unsynced > 0 || photosWaiting > 0;

  // Every parameter needs a score before submit - "" is the momentary state
  // while a box is being typed into, not a valid answer.
  const unscored = scoringRows.filter(
    (r) => r.score_obtained === "" || r.score_obtained == null || isNaN(Number(r.score_obtained))
  ).length;
  const scoringComplete = unscored === 0;

  const canSubmit = readiness && readiness.ready && !blockedBySync && scoringComplete && !busy;

  return (
    <div className="cap-writeup">
      <div className="wu-head">
        <div>
          <h2>Finish and submit</h2>
          <p>
            Submitting writes the observations and fixes the score. You can
            reopen it afterwards if you need to change something.
          </p>
        </div>
        <button type="button" className="small-btn" onClick={check} disabled={busy}>
          Re-check
        </button>
      </div>

      {error ? <p className="wu-error">{error}</p> : null}

      {/* gate 1 */}
      <div className={"submit-gate " + (blockedBySync ? "blocked" : "clear")}>
        <div>
          <strong>{blockedBySync ? "Not everything has synced" : "Everything has synced"}</strong>
          <span>
            {offline
              ? "You are offline. Your work is safe on this device — connect to a network to submit."
              : unsynced
              ? `${unsynced} change${unsynced === 1 ? "" : "s"} on this device have not reached the server yet.`
              : photosWaiting
              ? `${photosWaiting} photo${photosWaiting === 1 ? "" : "s"} ` +
                `${photosWaiting === 1 ? "is" : "are"} still on this device. ` +
                "Submitting now would leave them out of the report for good."
              : "Every change on this device is on the server."}
          </span>
        </div>
        {(unsynced || photosWaiting) && !offline ? (
          <button type="button" className="small-btn" onClick={syncFirst} disabled={busy}>
            {busy ? "Syncing…" : "Sync now"}
          </button>
        ) : null}
      </div>

      {/* gate 2 */}
      {!readiness ? (
        <div className="cap-loading">Checking…</div>
      ) : (
        <>
          <div className={"submit-gate " + (readiness.ready ? "clear" : "blocked")}>
            <div>
              <strong>{readiness.ready ? "Ready to submit" : "Not ready yet"}</strong>
              <span>
                {readiness.ready
                  ? "Every checkpoint is answered and every gap is written up."
                  : "These need attention first."}
              </span>
            </div>
          </div>

          {readiness.problems && readiness.problems.length ? (
            <ul className="submit-list problems">
              {readiness.problems.map((p, i) => (
                <li key={p.rule + i}>
                  <strong>{p.rule}</strong>
                  <span>{p.message || describe(p)}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {readiness.warnings && readiness.warnings.length ? (
            <ul className="submit-list warnings">
              {readiness.warnings.map((w, i) => (
                <li key={w.rule + i}>
                  <strong>{w.rule}</strong>
                  <span>{w.message || describe(w)}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}

      <ElectricalScoringTable rows={scoringRows} onChange={setScoringRows} />

      {/* gate 3 */}
      <div className={"submit-gate " + (scoringComplete ? "clear" : "blocked")}>
        <div>
          <strong>{scoringComplete ? "Scoring complete" : "Scoring not complete"}</strong>
          <span>
            {scoringComplete
              ? "Every parameter in the table above has a score."
              : `${unscored} parameter${unscored === 1 ? "" : "s"} in the table above still need${unscored === 1 ? "s" : ""} a score before you can submit.`}
          </span>
        </div>
      </div>

      <div className="scope-actions">
        <button type="button" className="cap-btn" disabled={!canSubmit} onClick={submit}>
          {busy ? "Submitting…" : "Submit this audit"}
        </button>
      </div>
    </div>
  );
}

/**
 * The server names a rule; turn it into something an auditor can act on.
 */
const RULE_TEXT = {
  unanswered: "Some checkpoints have no status yet.",
  missing_note: "Every non-compliant status needs an observation — the write-up screen collects them.",
  unassigned_photos:
    "Every photo must be attached to a checkpoint or discarded. The Photos tab lists them.",
  unconfirmed_carry_forward: "Some carried-over answers have not been confirmed.",
  unconfirmed_exclusion: "Some scope exclusions have not been confirmed.",
  unused_notes: "Some notes were never used — check none of them was a finding.",
};

function describe(p) {
  if (RULE_TEXT[p.rule]) return RULE_TEXT[p.rule];
  if (p.count != null) return `${p.count} item${p.count === 1 ? "" : "s"}`;
  return "";
}
